import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { User } from '../types/auth';
import { authApi } from '../services/api';

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name: string) => Promise<void>;
  logout: () => void;
  loginWithGoogle: () => void;
  loginWithDiscord: () => void;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in on app start
    console.log('AuthContext: Checking token on startup:', token ? 'exists' : 'not found');
    
    if (token) {
      // Verify token and get user info
      console.log('AuthContext: Verifying token...');
      authApi.getCurrentUser()
        .then((userData) => {
          console.log('AuthContext: Token verified, user data:', userData);
          setUser(userData);
        })
        .catch((error) => {
          console.log('AuthContext: Token verification failed:', error);
          localStorage.removeItem('token');
          queryClient.clear();
          setToken(null);
          setUser(null);
        })
        .finally(() => {
          console.log('AuthContext: Loading complete');
          setLoading(false);
        });
    } else {
      console.log('AuthContext: No token found, setting loading to false');
      setLoading(false);
    }
  }, [queryClient, token]);

  const login = async (email: string, password: string) => {
    console.log('AuthContext: Attempting login for:', email);
    const response = await authApi.login(email, password);

    queryClient.clear();
    localStorage.setItem('token', response.token);
    setToken(response.token);
    setUser(response.user);
    console.log('AuthContext: Login successful, user set:', response.user);
  };

  const register = async (email: string, password: string, name: string) => {
    const { user, token } = await authApi.register(email, password, name);
    queryClient.clear();
    localStorage.setItem('token', token);
    setToken(token);
    setUser(user);
  };

  const logout = () => {
    const activeToken = token;
    queryClient.clear();
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    if (activeToken) {
      void authApi.logout(activeToken).catch(() => {
        // JWT logout is currently stateless; local session cleanup above is authoritative.
      });
    }
  };

  const loginWithGoogle = () => {
    window.location.href = '/api/auth/google';
  };

  const loginWithDiscord = () => {
    window.location.href = '/api/auth/discord';
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    loginWithGoogle,
    loginWithDiscord,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
